import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CheckCircle2, Circle, ExternalLink, Download, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_META = {
  spotify: {
    label: 'Spotify',
    accent: '#1DB954',
    icon: 'fa-brands fa-spotify',
    verifyLabel: 'Verify follow via Spotify',
    helper: 'You will be redirected to Spotify to sign in and confirm you follow the artist.',
    mode: 'oauth',
  },
  soundcloud: {
    label: 'SoundCloud',
    accent: '#FF5500',
    icon: 'fa-brands fa-soundcloud',
    verifyLabel: 'I followed on SoundCloud',
    helper: 'Follow the artist on SoundCloud, then come back and confirm.',
    mode: 'honor',
  },
  instagram: {
    label: 'Instagram',
    accent: '#E1306C',
    icon: 'fa-brands fa-instagram',
    verifyLabel: 'I followed on Instagram',
    helper: 'Follow the artist on Instagram, then come back and confirm.',
    mode: 'honor',
  },
  tiktok: {
    label: 'TikTok',
    accent: '#25F4EE',
    icon: 'fa-brands fa-tiktok',
    verifyLabel: 'I followed on TikTok',
    helper: 'Follow the artist on TikTok, then come back and confirm.',
    mode: 'honor',
  },
};
const ORDER = ['spotify', 'soundcloud', 'instagram', 'tiktok'];

const useCooldownTick = () => {
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
};

const FangateModal = ({ open, album, fangateId, sessionId, spotifyBanner, spotifyReason = '', onClose, onUnlocked }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlockToken, setUnlockToken] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const initialised = useRef(false);
  useCooldownTick();

  const bootstrap = async (fgId) => {
    setLoading(true);
    try {
      let data;
      if (fgId) {
        data = (await axios.get(`${API}/fangate/${fgId}`)).data;
      } else {
        data = (
          await axios.post(`${API}/fangate/init`, { session_id: sessionId, album_id: album.id })
        ).data;
      }
      setState(data);
      if (data.unlocked_token) setUnlockToken(data.unlocked_token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start free download flow');
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      initialised.current = false;
      return;
    }
    if (initialised.current) return;
    initialised.current = true;
    bootstrap(fangateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fangateId]);

  useEffect(() => {
    if (!open || !spotifyBanner) return;
    if (spotifyBanner === 'ok') {
      toast.success('Spotify follow verified');
    } else if (spotifyBanner === 'notfollowing') {
      toast.error('You are not following Kehele Keff on Spotify yet — tap Follow in the Spotify app, wait a few seconds, and try again.');
    } else if (spotifyBanner === 'honor_fallback') {
      // Not an error — the backend just switched this session to click+confirm mode.
      // The next render will show the honor UI. Give the user a heads-up.
      toast('Spotify verification switched to manual confirm — tap Follow, wait a few seconds, then confirm below.', { duration: 6000 });
    } else {
      // spotifyBanner === 'error' — surface a helpful, actionable reason instead
      // of the generic "verification failed" message everyone has been reporting.
      const readable = {
        spotify_timeout:            'Spotify took too long to respond. Please try again.',
        follow_check_timeout:       'Spotify took too long to respond. Please try again.',
        token_exchange_failed:      'Spotify sign-in expired. Please tap Verify with Spotify again.',
        empty_token:                'Spotify sign-in expired. Please tap Verify with Spotify again.',
        session_not_found:          'This unlock session has expired — please refresh the page and try again.',
        missing_code:               'Spotify sign-in was cancelled. Please tap Verify with Spotify again.',
        access_denied:              'Spotify sign-in was cancelled. Please tap Verify with Spotify again.',
        spotify_api_401:            'Spotify sign-in expired. Please tap Verify with Spotify again.',
        spotify_api_403:            'Spotify blocked the follow check. Please retry in a moment.',
        spotify_api_429:            'Spotify is rate-limiting us. Wait ~30 seconds and try again.',
        follow_check_failed:        'Spotify verification hit a snag. Please try again.',
      }[spotifyReason] || 'Spotify verification failed. Please try again.';
      toast.error(readable);
    }
  }, [open, spotifyBanner, spotifyReason]);

  const completed = state
    ? ORDER.filter((p) => state[`${p}_ok`]).length
    : 0;
  const allDone = completed === 4;

  const handleClickSocial = async (platform, mode) => {
    const url = state?.socials?.[platform]?.url;
    if (!url) {
      toast.error(`No URL configured for ${platform}`);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    // For OAuth-mode Spotify we don't hit /click. For honor mode (including Spotify
    // when it's been flipped by a 401/403 fallback), we DO record the click.
    if (platform === 'spotify' && mode === 'oauth') return;
    try {
      const { data } = await axios.post(`${API}/fangate/click`, {
        fangate_id: state.id,
        platform,
      });
      setState((prev) => ({ ...prev, click_at: { ...(prev?.click_at || {}), [platform]: data.clicked_at } }));
    } catch (e) {
      // Non-fatal
    }
  };

  const handleConfirmSocial = async (platform) => {
    try {
      const { data } = await axios.post(`${API}/fangate/confirm`, {
        fangate_id: state.id,
        platform,
      });
      setState(data);
      toast.success(`${PLATFORM_META[platform].label} follow confirmed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Please wait for the cooldown to finish');
    }
  };

  const handleSpotifyVerify = () => {
    if (!state) return;
    window.location.href = `${API}/fangate/spotify/authorize?fangate_id=${state.id}`;
  };

  const remainingCooldown = (platform) => {
    const clickedAt = state?.click_at?.[platform];
    if (!clickedAt) return null;
    const elapsed = (Date.now() - new Date(clickedAt).getTime()) / 1000;
    const remaining = (state?.cooldown_seconds ?? 15) - elapsed;
    return remaining > 0 ? Math.ceil(remaining) : 0;
  };

  const handleUnlock = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.post(`${API}/fangate/${state.id}/unlock`);
      setUnlockToken(data.token);
      toast.success('Download unlocked');
      onUnlocked?.(data.token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to unlock. Ensure all four follows are confirmed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = async () => {
    if (!unlockToken) return;
    setDownloading(true);
    try {
      const { data } = await axios.get(
        `${API}/download/${unlockToken}?session_id=${encodeURIComponent(sessionId)}`
      );
      const usableTracks = (data?.tracks || []).filter((t) => t.has_file);
      if (usableTracks.length === 0) {
        toast.error('No downloadable files attached yet — please contact the artist');
        return;
      }
      const base = `${API}/download/${unlockToken}`;
      const q = `session_id=${encodeURIComponent(sessionId)}`;
      const url =
        usableTracks.length === 1
          ? `${base}/file?${q}&track=${usableTracks[0].index}`
          : `${base}/zip?${q}`;
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloading ${album.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download link no longer valid');
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="fangate-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-[#27272A] bg-[#0B0B0D] shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-[#71717A] hover:text-white hover:bg-white/5 transition-colors"
          data-testid="fangate-close-btn"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-6 pb-4 border-b border-[#18181B]">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] mb-1">Free Download</div>
          <h2 className="text-xl md:text-2xl font-heading font-medium">{album.title}</h2>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Follow all four socials to unlock. Every free release requires the full set.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-[#18181B] overflow-hidden">
              <div
                className="h-full bg-[#00F0FF] transition-all"
                style={{ width: `${(completed / 4) * 100}%` }}
                data-testid="fangate-progress-bar"
              />
            </div>
            <span className="text-xs text-[#71717A]" data-testid="fangate-progress-count">
              {completed}/4
            </span>
          </div>
        </div>

        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {loading && !state ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="animate-spin text-[#00F0FF]" size={20} />
            </div>
          ) : (
            ORDER.map((platform) => {
              const meta = PLATFORM_META[platform];
              const isDone = !!state?.[`${platform}_ok`];
              // Read mode PER SESSION — backend flips Spotify to 'honor' after a
              // 401/403 fallback so the modal renders the click+confirm UI for
              // that user's session without needing a page reload.
              const mode = state?.socials?.[platform]?.mode || meta.mode;
              const isOAuth = mode === 'oauth';
              const cd = isOAuth ? null : remainingCooldown(platform);
              const clicked = !!state?.click_at?.[platform];
              // Helper text — when Spotify is in honor fallback, override the
              // default OAuth helper with an honor-appropriate string.
              const helperText = platform === 'spotify' && mode === 'honor'
                ? 'Follow on Spotify, then come back and confirm.'
                : meta.helper;
              const verifyLabel = platform === 'spotify' && mode === 'honor'
                ? 'I followed on Spotify'
                : meta.verifyLabel;

              return (
                <div
                  key={platform}
                  className={`rounded-lg border p-3 transition-colors ${
                    isDone
                      ? 'border-[#00F0FF]/30 bg-[#00F0FF]/5'
                      : 'border-[#27272A] bg-[#0F0F11]'
                  }`}
                  data-testid={`fangate-row-${platform}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: `${meta.accent}20`, color: meta.accent }}
                    >
                      <i className={`${meta.icon} text-base`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{meta.label}</span>
                        {isDone ? (
                          <CheckCircle2
                            size={14}
                            className="text-[#00F0FF]"
                            data-testid={`fangate-check-${platform}`}
                          />
                        ) : (
                          <Circle size={14} className="text-[#3F3F46]" />
                        )}
                      </div>
                      <p className="text-[11px] text-[#71717A] mt-0.5">{helperText}</p>
                    </div>
                  </div>

                  {!isDone && (
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleClickSocial(platform, mode)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-[#27272A] bg-[#18181B] text-white hover:bg-[#27272A] transition-colors"
                        data-testid={`fangate-follow-${platform}`}
                      >
                        <ExternalLink size={12} /> Follow
                      </button>

                      {isOAuth ? (
                        <button
                          onClick={handleSpotifyVerify}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md text-black transition-colors"
                          style={{ background: meta.accent }}
                          data-testid={`fangate-verify-${platform}`}
                        >
                          {verifyLabel}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConfirmSocial(platform)}
                          disabled={!clicked || (cd ?? 1) > 0}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                            !clicked || (cd ?? 1) > 0
                              ? 'bg-[#18181B] text-[#52525B] cursor-not-allowed'
                              : 'bg-[#00F0FF] text-black hover:bg-[#33F3FF]'
                          }`}
                          data-testid={`fangate-confirm-${platform}`}
                        >
                          {!clicked
                            ? 'Click Follow first'
                            : cd > 0
                            ? `Wait ${cd}s`
                            : verifyLabel}
                        </button>
                      )}
                    </div>
                  )}

                  {/* When Spotify OAuth just failed, offer a retry (unless already verified). */}
                  {platform === 'spotify' && !isDone && isOAuth && spotifyBanner &&
                   spotifyBanner !== 'ok' && spotifyBanner !== 'honor_fallback' && (
                    <button
                      onClick={handleSpotifyVerify}
                      className="mt-2 w-full text-[11px] text-[#00F0FF] hover:underline"
                      data-testid="fangate-spotify-retry"
                    >
                      ↻ Retry Spotify verification
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-6 pt-4 border-t border-[#18181B]">
          {unlockToken ? (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#00F0FF] text-black font-medium hover:bg-[#33F3FF] transition-colors disabled:opacity-60"
              data-testid="fangate-download-btn"
            >
              {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
              Download free pack
            </button>
          ) : (
            <button
              onClick={handleUnlock}
              disabled={!allDone || downloading}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-medium transition-colors ${
                allDone
                  ? 'bg-white text-black hover:bg-[#E4E4E7]'
                  : 'bg-[#18181B] border border-[#27272A] text-[#52525B] cursor-not-allowed'
              }`}
              data-testid="fangate-unlock-btn"
            >
              {downloading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {allDone ? 'Unlock free download' : `Follow all 4 to unlock (${completed}/4)`}
            </button>
          )}
          <p className="text-[10px] text-center text-[#52525B] mt-2">
            Link expires 24 hours after unlock, tied to this browser.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FangateModal;
